import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2, AlertCircle, RefreshCw, MessageSquare, CheckCircle2 } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { formatDate } from '../../utils/dateFormatter';

/**
 * Candidate-initiated "request an update" / "request feedback" requests.
 * Direction is candidate -> recruiter, the reverse of the client-facing
 * request threading in CandidateRequests.jsx (which is a different data
 * model - request_messages threading vs a single response here), so this
 * is a separate, simpler panel rather than shoehorned into that chat UI.
 */
const CandidateUpdateRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/update-requests`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load requests');

      setRequests(data.requests || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching update requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (request) => {
    setSelected(request);
    setResponseText(request.response_message || '');
    setSubmitError(null);
  };

  const handleRespond = async () => {
    if (!selected || !responseText.trim()) return;
    try {
      setSubmitting(true);
      setSubmitError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/update-requests/${selected.id}/respond`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ response_message: responseText.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send response');

      setSelected(null);
      setResponseText('');
      await fetchRequests();
    } catch (err) {
      console.error('Error responding to request:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const responded = requests.filter((r) => r.status !== 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-6">
        <div className="w-full sm:w-80 space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">
            Pending ({pending.length})
          </h3>
          {pending.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing pending</p>
          )}
          {pending.map((r) => (
            <button
              key={r.id}
              onClick={() => handleSelect(r)}
              className={`w-full text-left rounded-md border p-3 text-sm hover:bg-muted/50 transition-colors ${
                selected?.id === r.id ? 'bg-muted border-primary' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium">
                  {r.application?.candidate?.first_name} {r.application?.candidate?.last_name}
                </span>
                <Badge variant="outline" className="text-xs">
                  {r.request_type === 'feedback' ? 'Feedback' : 'Update'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {r.application?.rfq?.ref_code} - {r.application?.rfq?.title}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDate(r.created_at, { style: 'short' })}
              </p>
            </button>
          ))}

          {responded.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-muted-foreground pt-4">
                Responded ({responded.length})
              </h3>
              {responded.map((r) => (
                <button
                  key={r.id}
                  onClick={() => handleSelect(r)}
                  className={`w-full text-left rounded-md border p-3 text-sm hover:bg-muted/50 transition-colors opacity-75 ${
                    selected?.id === r.id ? 'bg-muted border-primary opacity-100' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">
                      {r.application?.candidate?.first_name} {r.application?.candidate?.last_name}
                    </span>
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {r.application?.rfq?.ref_code} - {r.application?.rfq?.title}
                  </p>
                </button>
              ))}
            </>
          )}
        </div>

        <div className="flex-1">
          {selected ? (
            <Card>
              <CardContent className="p-4 space-y-4">
                <div>
                  <h3 className="font-semibold">
                    {selected.request_type === 'feedback' ? 'Feedback request' : 'Update request'} from{' '}
                    {selected.application?.candidate?.first_name} {selected.application?.candidate?.last_name}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {selected.application?.rfq?.ref_code} - {selected.application?.rfq?.title} · Application status
                    at request: {selected.application_status_at_request}
                  </p>
                </div>

                {selected.message && (
                  <div className="flex items-start gap-2 text-sm bg-muted/50 rounded-md p-3">
                    <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <p>{selected.message}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium block mb-1">
                    {selected.status === 'pending' ? 'Your response' : 'Response sent'}
                  </label>
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    rows={4}
                    disabled={selected.status !== 'pending'}
                    placeholder="Write a reply the candidate will see..."
                  />
                </div>

                {submitError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                )}

                {selected.status === 'pending' && (
                  <Button onClick={handleRespond} disabled={submitting || !responseText.trim()}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${submitting ? 'animate-spin' : ''}`} />
                    {submitting ? 'Sending...' : 'Send Response'}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm py-12">
              Select a request to respond
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateUpdateRequestsPanel;
